import React from 'react'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { IconTile } from './UI/Depth'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import routes from '../routes'
import sidebarIcons, { restaurantSidebarIcons } from './Sidebar/sidebarIcons'

/**
 * Title band shared by every "add / edit" card in the panel.
 *
 * `onClose` is passed only when the card is rendered inside a Modal, so the
 * close button appears exactly where there is a dialog to dismiss and stays
 * absent on the inline cards that sit above the listing tables. `action` is an
 * optional control (a switch, say) shown alongside the title, `icon` sets the
 * tinted tile in front of it (defaulting to the current page's sidebar icon),
 * and `subtitle` a line of explanation beneath.
 */
function FormCardHeader({
  title,
  subtitle,
  icon,
  onClose,
  action,
  className,
  sx
}) {
  const { t } = useTranslation()
  const location = useLocation()

  // `routes` imports the views, which import this component back, so guard
  // against the binding not being ready during module initialisation.
  const activeRoute = (routes || []).find(
    route =>
      route.path &&
      location.pathname.indexOf(`${route.layout}${route.path}`) !== -1
  )
  const Icon =
    icon ||
    (activeRoute &&
      ((activeRoute.layout === '/admin'
        ? restaurantSidebarIcons[activeRoute.name]
        : sidebarIcons[activeRoute.name]) ||
        activeRoute.icon))
  const hasIcon =
    !!Icon && (typeof Icon === 'function' || typeof Icon === 'object')

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        ...sx
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.75,
          minWidth: 0
        }}>
        {hasIcon ? (
          <IconTile
            icon={Icon}
            size={44}
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          />
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: 17,
              fontWeight: 700,
              color: 'text.primary',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography sx={{ fontSize: 13, color: 'text.disabled', mt: 0.25 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Box>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        {action}
        {onClose ? (
          <Tooltip title={t('Close')}>
            <IconButton
              aria-label={t('Close')}
              onClick={onClose}
              size="small"
              sx={{
                color: 'text.disabled',
                '&:hover': { color: 'text.primary', bgcolor: 'grey.100' }
              }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  )
}

export default FormCardHeader
